import * as api from './vatsApi';
import type { Account, Vat } from './vatsApi';
import { ConfirmationModal, ModalProvider, useModal } from '../generalcomponents/ModalProvider';
import { DetailViewShell, FormRow, sx13} from '../common/detailView';
import { FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState  } from 'react';
import { useDetailView } from '../common/useDetailView';
import { useTranslation } from 'react-i18next';

interface Props {
    vatId: number | null;
}

export function VatView(props: Props) {
    return (
        <ModalProvider>
            <VatViewInner {...props} />
        </ModalProvider>
    );
}

function VatViewInner({ vatId }: Props) {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const detail = useDetailView<Vat>();
    const [accounts, setAccounts] = useState<Account[]>([]);

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const accs = await api.getAccounts();
            setAccounts(accs);
            if (vatId === null) {
                detail.setItem({ id: 0, vatCode: '', vatName: '', vatRate: 0, account: null });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getVat(vatId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [vatId]);

    useEffect(() => { load(); }, [load]);

    const isVatCodeValid = !!detail.item?.vatCode;

    const save = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            const response = detail.isNew ?
                await api.createVat(detail.item!) :
                await api.updateVat(detail.item!);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = () => {
        if (!detail.item) return;
        openModal(
            <ConfirmationModal
                header={t('common.delete')}
                message={t('vat.confirmDelete')}
                onModalResponse={(confirmed) => { if (confirmed) performDelete(); }}
            />
        );
    };

    const performDelete = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            await api.deleteVat(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<Vat>
            featureKey="devFeature_reactVatView"
            title={`${detail.item?.vatCode ?? ''} ${detail.item?.vatName ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isVatCodeValid}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (<>
                <FormRow label={t('vat.vatCode')}>
                    {!detail.isNew && !detail.isEditing ? (
                        <Typography sx={sx13}>{item.vatCode}</Typography>
                    ) : (
                        <TextField size="small" fullWidth
                            inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                            value={item.vatCode}
                            onChange={(e) => detail.update('vatCode', e.target.value.toUpperCase())}
                            error={!isVatCodeValid} />
                    )}
                </FormRow>

                <FormRow label={t('vat.vatName')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{item.vatName}</Typography>
                    ) : (
                        <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                            value={item.vatName ?? ''}
                            onChange={(e) => detail.update('vatName', e.target.value)} />
                    )}
                </FormRow>

                <FormRow label={t('vat.vatRate')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>
                            {item.vatRate != null ? `${(item.vatRate * 100).toLocaleString()} %` : ''}
                        </Typography>
                    ) : (
                        <TextField size="small" type="number" fullWidth
                            value={item.vatRate ?? 0}
                            onChange={(e) => detail.update('vatRate', e.target.value === '' ? 0 : Number(e.target.value))} />
                    )}
                </FormRow>

                <FormRow label={t('vat.account')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>
                            {item.account ? `${item.account.accountNumber} ${item.account.accountName}` : ''}
                        </Typography>
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select
                                value={item.account?.id ?? ''}
                                onChange={(e) => {
                                    const a = accounts.find((x) => x.id === e.target.value);
                                    detail.update('account', a ?? null);
                                }}
                            >
                                {[...accounts]
                                    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                    .map((a) => (
                                        <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                </FormRow>
            </>)}
        </DetailViewShell>
    );
}
